import { Link } from "react-router-dom";
import { HeartHandshake } from "lucide-react";

const SECTIONS = [
  {
    title: "The service",
    body: "RoutineBuddy is a product of Simplerlivez LTD. It provides visual routines, communication boards, timers, care notes and related tools for carers and care organisations supporting people with additional needs. RoutineBuddy is an organisational aid — it is not a medical device, does not provide medical advice, diagnosis or treatment, and is not a substitute for professional care judgement or emergency services.",
  },
  {
    title: "Your account",
    body: "You must provide accurate registration details and keep your password and carer PIN confidential. You are responsible for activity on your account. You must be 18 or older to create an account; the app is used by people you support under your supervision.",
  },
  {
    title: "Subscriptions and trials",
    body: "Premium (£5/month or £50/year) and Organisation (£15/month or £150/year) plans are billed in advance via Stripe. New subscribers get a free trial (14 days Premium, 30 days Organisation, once per account or organisation); unless you cancel before the trial ends, the paid plan starts automatically. You can cancel anytime from the billing portal — access continues until the end of the paid period. Prices may change with 30 days' notice; changes apply from your next renewal.",
  },
  {
    title: "Referral rewards",
    body: "When someone subscribes using your invite link, you receive one free month (applied as a subscription discount, or as 30 days of Premium access if you don't have a subscription). Rewards have no cash value, can't be transferred, and may be withdrawn if we detect abuse (e.g. self-referrals or fake accounts).",
  },
  {
    title: "Acceptable use",
    body: "Don't use RoutineBuddy unlawfully; don't upload content you have no right to record; don't enter other people's sensitive identifiers (NHS numbers, payment details) into AI prompts; don't attempt to access other accounts' data; don't use the public API beyond reasonable volumes or resell access. We may suspend accounts that breach these terms.",
  },
  {
    title: "Your content",
    body: "You keep ownership of everything you create or upload. You grant Simplerlivez LTD a licence to store, process and transmit it solely to provide the service (including sharing with the people you explicitly share it with). You confirm you have the right to record information about the people you support (consent or another lawful basis, e.g. as their carer or care provider).",
  },
  {
    title: "AI-generated content",
    body: "AI features generate suggestions (routines, summaries, pictures, stories). They can be wrong. Always review AI output before relying on it, especially anything extracted from care plans. You remain responsible for the care decisions you make.",
  },
  {
    title: "Availability and changes",
    body: "We aim for high availability but don't guarantee uninterrupted service; web/PWA alarms only fire while the app is open (native-app alarms are in development). We may update features with notice where changes are significant.",
  },
  {
    title: "Liability",
    body: "Nothing in these terms excludes liability that can't be excluded by law (including for death or personal injury caused by negligence). Otherwise, to the extent permitted by law, Simplerlivez LTD is not liable for indirect or consequential losses, and our total liability is limited to the fees you paid in the 12 months before the claim. We are not liable for care outcomes; RoutineBuddy is a record-keeping and prompting aid only.",
  },
  {
    title: "Ending the service",
    body: "You can delete your account anytime from the dashboard — this removes your data permanently. We may terminate accounts for material breach after notice where practicable.",
  },
  {
    title: "Governing law and contact",
    body: "These terms are governed by the laws of England and Wales, and disputes are subject to the courts of England and Wales. Contact: hello@simplerlivez.com (Simplerlivez LTD).",
  },
];

export default function TermsPage() {
  return (
    <div data-testid="terms-page" className="min-h-screen bg-background">
      <header className="max-w-3xl mx-auto px-4 py-4">
        <Link to="/" data-testid="terms-home-link" className="flex items-center gap-2 w-fit">
          <span className="w-10 h-10 rounded-2xl bg-primary grid place-items-center">
            <HeartHandshake size={24} strokeWidth={2.5} className="text-white" />
          </span>
          <span className="text-xl font-extrabold tracking-tight">RoutineBuddy</span>
        </Link>
      </header>
      <main className="max-w-3xl mx-auto px-4 py-8 pb-24">
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Terms of service</h1>
        <p className="mt-2 text-lg text-muted-foreground">Last updated: September 2026. Plain English on purpose.</p>
        <div className="mt-8 space-y-6">
          {SECTIONS.map((s) => (
            <section key={s.title} className="bg-white rounded-3xl border-2 border-foreground/10 p-6">
              <h2 className="text-xl font-extrabold">{s.title}</h2>
              <p className="mt-2 text-lg leading-relaxed text-muted-foreground">{s.body}</p>
            </section>
          ))}
        </div>
      </main>
    </div>
  );
}
